/**
 * Test-run modal for the workflow editor.
 *
 * - Opens as a CENTERED modal (Dialog) when "Test run" is clicked in the
 *   editor topbar — aligned with the node-scoped "Execute Node" prompt so
 *   both run surfaces present the same way. (Was a right-side Sheet drawer.)
 * - Trigger-strategy banner + saved-sample picker + JSON payload editor +
 *   save-as + the Run button. It's a pure INPUT surface: on a successful run
 *   the modal auto-closes and the bottom Logs dock (RunDetailsPanel) owns the
 *   per-node trace/output.
 * - The "Run" button POSTs the current canvas DAG as `definition` so the
 *   user can test edits without saving a new version. The backend rejects
 *   shapes inconsistent with the workflow header (direction / canonical).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Play, Save, Trash2 } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WebhookListenDialog } from "@/components/workflows/WebhookListenDialog";
import {
  workflowsApi,
  type TestRunNodeTrace,
  type TriggerStrategy,
  type WorkflowDefinition,
} from "@/lib/api-workflows";
import { usePermissions } from "@/lib/permissions";

export interface TestRunDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  /**
   * Live snapshot of the editor canvas, sent as `definition` in the
   * test-run request so unsaved edits are testable.
   */
  buildDefinition: () => WorkflowDefinition;
  /**
   * Notify the editor which nodes ended up failed / succeeded so it can
   * highlight them in the canvas. Called with empty list when the
   * drawer is cleared.
   */
  onTraceUpdate?: (trace: TestRunNodeTrace[]) => void;
  /**
   * Hand the editor the full TestRunResponse so it can populate the
   * bottom RunDetailsPanel with the result — avoids the user having to
   * keep this drawer open just to read traces.
   */
  onRunComplete?: (result: import("@/lib/api-workflows").TestRunResponse) => void;
  /**
   * Fired RIGHT BEFORE the POST is sent. The editor uses it to arm
   * its SSE listener so it can latch onto the execution_id as soon
   * as the EXECUTION_STARTED event lands (which races the response).
   */
  onRunStart?: () => void;
  /**
   * n8n-style step debug: when set, the run halts AFTER this node id
   * and the resulting execution becomes the "pinned" anchor. The
   * drawer shows a banner with the node label so the user sees what
   * they're about to do. The editor passes the label via ``stopAfterNodeLabel``.
   */
  stopAfterNodeId?: string | null;
  stopAfterNodeLabel?: string | null;
  /**
   * Resume from a pinned execution. When set, ``payload`` is ignored
   * server-side — the parent's initial_payload is replayed. The drawer
   * hides the payload textarea and shows a banner instead.
   */
  resumeFromExecutionId?: string | null;
}

export function TestRunDrawer({
  open,
  onOpenChange,
  workflowId,
  buildDefinition,
  onTraceUpdate,
  onRunComplete,
  onRunStart,
  stopAfterNodeId,
  stopAfterNodeLabel,
  resumeFromExecutionId,
}: TestRunDrawerProps) {
  const { can } = usePermissions();
  const canRun = can("workflows", "execute");
  const qc = useQueryClient();
  const [payloadText, setPayloadText] = React.useState<string>('{\n  \n}');
  const [parseError, setParseError] = React.useState<string | null>(null);
  const [selectedSample, setSelectedSample] = React.useState<string>("");
  const [saveName, setSaveName] = React.useState<string>("");
  // Listen-for-test-event overlay (n8n flow). Only relevant when the
  // chosen trigger is an external_hit (webhook). Mounting the dialog
  // mints a one-shot URL on the backend; unmounting (Stop / X) fires
  // DELETE so the slot frees up immediately.
  const [listeningWebhook, setListeningWebhook] = React.useState(false);
  // Token of the active test-webhook capture, when one happened.
  // The capture endpoint is parked waiting for our test-run to finish;
  // once it does, we POST the final_payload back so the curl caller
  // gets the workflow output instead of just {received: true}.
  const [pendingRespondToken, setPendingRespondToken] = React.useState<
    string | null
  >(null);
  // ADR-0065 T1-4 — opt-in: also fire the configured error workflow when this
  // test-run FAILS (off by default; a manual test-run doesn't trigger error
  // workflows, n8n parity). Only shown when the workflow has one configured.
  const [fireErrorWorkflow, setFireErrorWorkflow] = React.useState(false);
  // Local "run submitting" flag that drives the Run button spinner —
  // deliberately SEPARATE from `runMutation.isPending`. The test-run POST is a
  // synchronous, effectively-uncancellable request: after the user hits Stop on
  // the canvas the backend execution is cancelled but this promise stays pending
  // until the whole run unwinds, so `isPending` would leave the Run button stuck
  // spinning if the drawer is re-opened. We reset THIS flag whenever the drawer
  // closes (the editor's canvas Stop button owns the run's lifecycle from then
  // on), so re-opening always shows a fresh, clickable Run button.
  const [submitting, setSubmitting] = React.useState(false);
  React.useEffect(() => {
    if (!open) setSubmitting(false);
  }, [open]);

  // Trigger strategies (B) — drives whether we ask the user for a
  // payload at all. Refetched on open AND every time the canvas
  // definition shape changes (we hash the source nodes to detect
  // structural changes cheaply).
  const definitionForStrategy = React.useMemo(
    () => (open ? buildDefinition() : null),
    // We intentionally don't put `buildDefinition` in deps — it's
    // a closure that captures the latest canvas state every call.
    // Re-running when `open` flips is enough; the user can hit
    // Run multiple times in the same open session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );
  const strategiesQuery = useQuery({
    queryKey: [
      "trigger-strategies",
      workflowId,
      // Source node ids are the smallest stable key — full definition
      // would re-fetch on every minor edit.
      (definitionForStrategy?.nodes ?? [])
        .filter((n) => n.kind.endsWith("_source") || n.kind.endsWith("_trigger"))
        .map((n) => `${n.id}:${n.kind}`)
        .join(","),
    ],
    queryFn: () =>
      workflowsApi.triggerStrategies(workflowId, {
        definition: definitionForStrategy ?? undefined,
      }),
    enabled: open && definitionForStrategy != null,
  });

  // When there's more than one source, the user has to pick which to
  // run from. Single-source DAGs auto-resolve.
  const strategies = strategiesQuery.data?.strategies ?? [];
  const [chosenTriggerId, setChosenTriggerId] = React.useState<string | null>(
    null,
  );
  React.useEffect(() => {
    // Default to the first (and usually only) source when the list
    // arrives or changes shape.
    if (strategies.length > 0) {
      setChosenTriggerId((prev) =>
        prev && strategies.some((s) => s.node_id === prev)
          ? prev
          : strategies[0].node_id,
      );
    } else {
      setChosenTriggerId(null);
    }
  }, [strategies]);
  const chosenStrategy =
    strategies.find((s) => s.node_id === chosenTriggerId) ?? null;

  // Pre-fill the payload editor with the synthesised value so the
  // user sees exactly what'll be sent. Stays editable so they can
  // tweak the timestamp if they want to test "what if the cron
  // fired yesterday".
  React.useEffect(() => {
    if (
      chosenStrategy?.kind === "auto_payload" &&
      chosenStrategy.payload !== undefined
    ) {
      setPayloadText(JSON.stringify(chosenStrategy.payload, null, 2));
    }
  }, [chosenStrategy]);

  const samplesQuery = useQuery({
    queryKey: ["workflow-samples", workflowId],
    queryFn: () => workflowsApi.listSamples(workflowId),
    enabled: open,
  });

  const saveSampleMutation = useMutation({
    mutationFn: (name: string) => {
      let payload: unknown;
      try {
        payload = JSON.parse(payloadText);
      } catch (e) {
        throw new Error(e instanceof Error ? e.message : "invalid JSON");
      }
      return workflowsApi.upsertSample(workflowId, { name, payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflow-samples", workflowId] });
      setSaveName("");
    },
  });

  const deleteSampleMutation = useMutation({
    mutationFn: (sampleId: string) =>
      workflowsApi.deleteSample(workflowId, sampleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflow-samples", workflowId] });
      setSelectedSample("");
    },
  });

  const runMutation = useMutation({
    mutationFn: async () => {
      // Resume runs ignore the payload textarea entirely — the backend
      // replays the parent execution's initial_payload. Skip JSON
      // parsing so a blank/dirty editor doesn't fail the run.
      let payload: unknown = null;
      if (!resumeFromExecutionId) {
        try {
          payload = JSON.parse(payloadText);
        } catch (e) {
          const msg = e instanceof Error ? e.message : "invalid JSON";
          setParseError(msg);
          throw new Error(msg);
        }
        setParseError(null);
      } else {
        setParseError(null);
      }
      const definition = buildDefinition();
      // Arm the editor's live SSE listener BEFORE the POST goes out:
      // the executor publishes EXECUTION_STARTED + node_started events
      // mid-flight, racing the response. If we arm after, we miss the
      // first events.
      onRunStart?.();
      return workflowsApi.testRun(workflowId, {
        payload,
        definition,
        trigger_node_id: chosenTriggerId ?? undefined,
        stop_after_node_id: stopAfterNodeId ?? undefined,
        resume_from_execution_id: resumeFromExecutionId ?? undefined,
        fire_error_workflow: fireErrorWorkflow || undefined,
      });
    },
    onSuccess: (resp) => {
      onTraceUpdate?.(resp.node_traces);
      onRunComplete?.(resp);
      // Listen-for-test flow: if this run was kicked off by a webhook
      // capture, the test-capture endpoint is parked waiting for the
      // result. Publish it back so the curl caller sees the workflow
      // output instead of just {"received": true}. Fire-and-forget;
      // a failed publish (e.g. token expired) is best-effort.
      if (pendingRespondToken) {
        workflowsApi
          .respondTestWebhook(workflowId, pendingRespondToken, {
            status_code: resp.status === "succeeded" ? 200 : 500,
            body:
              resp.status === "succeeded"
                ? resp.final_payload
                : { error: resp.error ?? "workflow_failed" },
          })
          .catch(() => undefined);
        setPendingRespondToken(null);
      }
      // Auto-close so the user immediately sees the canvas with the
      // per-node statuses lit up. The trace remains on the canvas
      // (owned by the editor's traceStatus state — see onTraceUpdate)
      // until the user fires another run or wipes it explicitly.
      onOpenChange(false);
    },
    onError: () => {
      // On failure we keep the drawer open so the user can see the
      // error message (runMutation.error) + tweak the payload + retry
      // without losing their input; the editor's canvas overlay + the
      // bottom Logs dock still hold the last valid trace. Re-enable the
      // Run button so the retry is possible.
      setSubmitting(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:!max-w-2xl flex flex-col">
        <DialogHeader>
          {/* DialogContent already renders a close X in the top-right
              corner — no need to duplicate it here. */}
          <DialogTitle className="text-sm font-semibold">Test run</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3 space-y-0">
          {/* Trigger strategy banner (B). Adapts the dialog to the
              source kind: cron gets an auto-payload notice + editable
              preview; webhook gets a URL + curl card; manual gets the
              plain JSON editor (the default). When the DAG has more
              than one source the user must pick. */}
          <TriggerStrategyHeader
            strategies={strategies}
            chosenId={chosenTriggerId}
            onChoose={setChosenTriggerId}
          />

          {/* Step-debug banners: tell the user when this run will halt
              at a specific node, OR is a resume from a pinned parent. */}
          {stopAfterNodeId && (
            <Alert tone="info" title="Step debug">
              Halts after <span className="font-mono">{stopAfterNodeLabel ?? stopAfterNodeId}</span>.
              Downstream nodes won't run. The resulting execution becomes
              the pinned anchor.
            </Alert>
          )}
          {resumeFromExecutionId && (
            <Alert tone="info" title="Resume from pinned run">
              Replaying the parent execution's initial payload —
              the payload editor below is disabled.
            </Alert>
          )}
          {/* Saved samples picker */}
          {(samplesQuery.data?.length ?? 0) > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="testrun-sample">Saved sample</Label>
              <div className="flex items-center gap-2">
                <select
                  id="testrun-sample"
                  className="flex-1 border border-border bg-background px-2 py-1.5 text-xs"
                  value={selectedSample}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedSample(id);
                    if (!id) return;
                    const s = samplesQuery.data?.find((x) => x.id === id);
                    if (s) {
                      setPayloadText(JSON.stringify(s.payload, null, 2));
                      setSaveName(s.name);
                    }
                  }}
                >
                  <option value="">— pick one to load —</option>
                  {(samplesQuery.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {canRun && selectedSample && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteSampleMutation.mutate(selectedSample)}
                    title="Delete this sample"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="testrun-payload">Payload (JSON)</Label>
            <textarea
              id="testrun-payload"
              rows={8}
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              className="block w-full resize-y border border-border bg-background px-2 py-1.5 font-mono text-xs"
              spellCheck={false}
            />
            {parseError && (
              <p className="text-xs text-destructive">
                Invalid JSON: {parseError}
              </p>
            )}
          </div>

          {/* Save current payload as a sample */}
          {canRun && (
            <div className="flex items-end gap-2 border-t border-border pt-2">
              <div className="flex-1 space-y-1">
                <Label htmlFor="testrun-savename" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Save current payload as
                </Label>
                <Input
                  id="testrun-savename"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g. happy-path / edge-case-empty-lines"
                  className="h-8 text-xs"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={
                  !saveName.trim() || saveSampleMutation.isPending
                }
                onClick={() => saveSampleMutation.mutate(saveName.trim())}
              >
                {saveSampleMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" strokeWidth={1.5} />
                )}
                Save
              </Button>
            </div>
          )}
          {saveSampleMutation.isError && (
            <p className="text-xs text-destructive">
              Save failed: {(saveSampleMutation.error as Error)?.message}
            </p>
          )}

          {/* Run button. For external_hit (webhook) triggers the
              regular Run would just send the manually-typed payload
              like a fake test — useful, but the user expects the
              n8n flow: mint a URL, listen, capture. We surface BOTH
              as siblings so the user can pick: "Listen" for real,
              or "Run" to fake it from the JSON editor. */}
          {chosenStrategy?.kind === "external_hit" && (
            <Button
              onClick={() => setListeningWebhook(true)}
              variant="outline"
              className="w-full"
            >
              <Play className="h-4 w-4" strokeWidth={1.5} />
              Listen for test event
            </Button>
          )}
          {/* ADR-0065 T1-4 — opt-in error-workflow fire on a failed test-run.
              Only shown when the workflow has one configured (a manual test-run
              doesn't trigger it by default — n8n parity). */}
          {definitionForStrategy?.error_workflow_id && !resumeFromExecutionId && (
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={fireErrorWorkflow}
                onChange={(e) => setFireErrorWorkflow(e.target.checked)}
              />
              <span>
                In caso di fallimento, esegui anche il workflow di errore
                configurato (lo lancia davvero, come in produzione).
              </span>
            </label>
          )}
          <Button
            onClick={() => {
              setSubmitting(true);
              runMutation.mutate();
            }}
            disabled={submitting}
            className="w-full"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <Play className="h-4 w-4" strokeWidth={1.5} />
            )}
            {chosenStrategy?.kind === "external_hit"
              ? "Run with fake payload"
              : "Run"}
          </Button>

          {/* Listen overlay — only mounted while active so the long-
              poll doesn't run in the background after Stop. */}
          {listeningWebhook && chosenStrategy?.kind === "external_hit" && (
            <WebhookListenDialog
              strategy={chosenStrategy}
              workflowId={workflowId}
              titlePrefix="Test Run"
              onCancel={() => setListeningWebhook(false)}
              onCapture={(body, token) => {
                setListeningWebhook(false);
                // Park the token: runMutation.onSuccess will publish
                // the final_payload back to it so the curl caller
                // gets the workflow output.
                setPendingRespondToken(token);
                // Stuff the captured body into the JSON editor so
                // the user sees what arrived, THEN fire the run.
                // The run mutation will JSON.parse this back out;
                // round-tripping is safe because the backend
                // serialised the body as JSON.
                setPayloadText(JSON.stringify(body, null, 2));
                setSubmitting(true);
                runMutation.mutate();
                // CLOSE THE DRAWER IMMEDIATELY so the user can watch
                // the canvas light up live (per-node SSE events fire
                // mid-run; if the drawer stayed open until the
                // workflow completed, a 30s wait node would mean
                // 30s of "where's my run going?" UX). The mutation
                // continues in the QueryClient independent of this
                // component's lifecycle — onTraceUpdate and
                // onRunComplete still fire on success, populating
                // the canvas overlay and the bottom logs panel.
                onOpenChange(false);
              }}
            />
          )}

          {runMutation.isError && !parseError && (
            <Alert tone="danger">
              {(runMutation.error as Error)?.message ?? "Test-run failed"}
            </Alert>
          )}

          {/* Results are NOT shown here — the modal auto-closes on Run and
              the bottom Logs dock (RunDetailsPanel) owns the trace/output. */}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// TriggerStrategyHeader — adapts the dialog to the source kind (B).
// ---------------------------------------------------------------------------
//
// Renders an info banner at the top of the test-run drawer that
// changes shape based on the chosen trigger:
//
//   * **auto_payload** (schedule/cron) — small info note that the
//     payload below is auto-generated; the textarea stays editable
//     so the operator can tweak the timestamp for "what if it
//     fired yesterday" experiments.
//   * **manual_input** — no banner at all (default UX, the JSON
//     editor below is exactly what the user expects).
//   * **external_hit** (webhook) — URL + curl card; running from
//     here doesn't make sense, so we explain why and point at the
//     real flow.
//   * **poll_once** — explanation that v1 doesn't auto-invoke
//     adapters, recommends manual JSON instead.
//
// Multi-source DAGs get a trigger selector so the operator can pick
// which source to fire from.


function TriggerStrategyHeader({
  strategies,
  chosenId,
  onChoose,
}: {
  strategies: TriggerStrategy[];
  chosenId: string | null;
  onChoose: (id: string) => void;
}) {
  if (strategies.length === 0) return null;
  const chosen = strategies.find((s) => s.node_id === chosenId) ?? strategies[0];

  return (
    <div className="space-y-2">
      {strategies.length > 1 && (
        <div className="space-y-1">
          <Label htmlFor="testrun-trigger" className="text-xs">
            Trigger
            <span className="ml-1 text-[10px] text-muted-foreground">
              (this workflow has {strategies.length} source nodes)
            </span>
          </Label>
          <select
            id="testrun-trigger"
            value={chosenId ?? ""}
            onChange={(e) => onChoose(e.target.value)}
            className="block w-full border border-border bg-background px-2 py-1.5 text-xs"
          >
            {strategies.map((s) => (
              <option key={s.node_id} value={s.node_id}>
                {s.label} ({s.node_kind})
              </option>
            ))}
          </select>
        </div>
      )}
      <StrategyBanner strategy={chosen} />
    </div>
  );
}


function StrategyBanner({ strategy }: { strategy: TriggerStrategy }) {
  if (strategy.kind === "manual_input") {
    // Default UX — no banner needed.
    return null;
  }
  if (strategy.kind === "auto_payload") {
    return (
      <Alert tone="info" title={`${strategy.label} — auto-generated payload`}>
        <p className="text-xs">{strategy.description}</p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          The JSON editor below is pre-filled with what the scheduler
          would emit. Tweak it if you want to simulate a different
          moment in time.
        </p>
      </Alert>
    );
  }
  if (strategy.kind === "external_hit") {
    // Substitute <host> with the current window location so the URL
    // is directly usable. We keep the literal placeholder if the
    // window object isn't available (SSR / tests).
    const resolvedUrl = strategy.external_url
      ? strategy.external_url.replace(
          "<host>",
          typeof window !== "undefined"
            ? `${window.location.protocol}//${window.location.host}`
            : "<host>",
        )
      : null;
    return (
      <Alert tone="warning" title={`${strategy.label} — external trigger`}>
        <p className="text-xs">{strategy.description}</p>
        {resolvedUrl && (
          <div className="mt-2 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              URL
            </div>
            <code className="block break-all rounded bg-muted/40 px-2 py-1 font-mono text-[11px]">
              {resolvedUrl}
            </code>
          </div>
        )}
        {strategy.curl_example && (
          <div className="mt-2 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              curl
            </div>
            <pre className="overflow-x-auto rounded bg-muted/40 px-2 py-1 font-mono text-[10px]">
              {(strategy.curl_example ?? "").replace(
                "<host>",
                typeof window !== "undefined"
                  ? `${window.location.protocol}//${window.location.host}`
                  : "<host>",
              )}
            </pre>
          </div>
        )}
        <p className="mt-2 text-[11px] text-muted-foreground">
          The JSON editor below still works if you want to fake the
          payload locally instead — useful for iterating on downstream
          nodes without hitting the real webhook every time.
        </p>
      </Alert>
    );
  }
  if (strategy.kind === "poll_once") {
    return (
      <Alert tone="warning" title={`${strategy.label} — polling trigger`}>
        <p className="text-xs">{strategy.description}</p>
        {strategy.hint && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            {strategy.hint}
          </p>
        )}
      </Alert>
    );
  }
  return null;
}
